import React from "react";
import { RotateCcw } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { healStale, isChunkError, reportUiError } from "@/lib/selfheal";

const HealFallback = ({ name, onRetry }) => {
  const { t } = useI18n();
  return (
    <div role="alert" className="mx-auto my-10 max-w-xl rounded-2xl border border-[rgba(245,197,66,0.3)] bg-[rgba(74,6,11,0.4)] p-6 text-center" data-testid={`heal-fallback-${name}`}>
      <p className="font-display text-lg font-bold text-white">{t("heal.crashTitle")}</p>
      <p className="mt-2 text-sm text-neutral-300">{t("heal.crashText")}</p>
      <button type="button" onClick={onRetry} className="btn-gold btn-sm mt-4" data-testid={`heal-retry-btn-${name}`}>
        <RotateCcw className="h-4 w-4" /> {t("heal.retry")}
      </button>
    </div>
  );
};

// Crashed section: reported, remounted once automatically, then a "Réessayer" button. Resets on navigation (resetKey).
export class HealBoundary extends React.Component {
  state = { error: null, remounts: 0, gen: 0 };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const component = info?.componentStack?.match(/at (\w+)/)?.[1] || this.props.name;
    reportUiError({ component, message: String(error?.message || error), kind: isChunkError(error) ? "chunk" : "crash" });
    window.dispatchEvent(new Event("fmo:heal-caught"));
    if (isChunkError(error)) {
      healStale(String(error?.message || error));
      return;
    }
    if (this.state.remounts < 1) this.setState((s) => ({ error: null, remounts: s.remounts + 1, gen: s.gen + 1 }));
  }

  componentDidUpdate(prev) {
    if (prev.resetKey !== this.props.resetKey && (this.state.error || this.state.remounts)) {
      this.setState((s) => ({ error: null, remounts: 0, gen: s.gen + 1 }));
    }
  }

  retry = () => this.setState((s) => ({ error: null, gen: s.gen + 1 }));

  render() {
    const { name, children } = this.props;
    if (this.state.error) return <HealFallback name={name} onRetry={this.retry} />;
    return (
      <div className="contents" data-testid={`heal-boundary-${name}`} data-remounts={this.state.remounts}>
        <React.Fragment key={this.state.gen}>{children}</React.Fragment>
      </div>
    );
  }
}
